import React, { useState, useEffect } from 'react';
import { api } from './api/api';
import generateReceiptPdf from './generateReceiptPdf';
import generateBolInvoicePdf from './generateBolInvoicePdf';

// Helper to parse JSON arrays or single IDs from DB
const parseIdArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch { return trimmed.split(",").map((item) => item.trim()).filter(Boolean); }
  }
  return [value];
};

const BOLIssuanceScreen = () => {
  const emptyForm = {
    bolReference: '',
    bolNature: '',
    bolTypeCode: '',
    notifyName: '',
    notifyAddress: '',
    customerName: '',
    numberOfPackages: '',
    packageTypeCode: '',
    grossMass: '',
    shippingMarks: '',
    goodsDescription: '',
    cargoTypes: [],
    services: [],
    packingItems: [],
    buildUpItems: [],
    services: [],
    packingItems: [],
    buildUpItems: [],
    cargoType: '',
    serviceType: '',
    serviceId: null,
    packingItemId: null,
    buildUpItemId: null,
    storageStartDate: '',
    storageEndDate: '',
    homes: [],
    day: false
  };

  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isDirectIssuance, setIsDirectIssuance] = useState(false);
  const [editingBol, setEditingBol] = useState(null);
  const [bols, setBols] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [servicesList, setServicesList] = useState([]);
  const [cargoTypes, setCargoTypes] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [calculatedFees, setCalculatedFees] = useState({ service: 0, packing: 0, buildUp: 0, total: 0 });

  const parseJsonValue = (value) => {
    if (!value) return {};
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (err) {
      return {};
    }
  };

  const normalizeType = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

  const findServiceByTypeAndWeight = (serviceType, weight) => {
    if (!serviceType) return null;
    const normalizedType = normalizeType(serviceType);
    const matchingType = servicesList.filter((s) => normalizeType(s.type) === normalizedType);
    if (matchingType.length === 0) return null;

    const sorted = matchingType
      .map((s) => ({
        ...s,
        rangeFrom: Number(s.range_from ?? s.rangeFrom ?? 0),
        rangeTo: Number(s.range_to ?? s.rangeTo ?? Infinity)
      }))
      .sort((a, b) => a.rangeFrom - b.rangeFrom);

    const byWeight = sorted.find((s) => {
      const from = Number.isFinite(s.rangeFrom) ? s.rangeFrom : 0;
      const to = Number.isFinite(s.rangeTo) ? s.rangeTo : Infinity;
      return weight >= from && weight <= to;
    });

    return byWeight || sorted[0] || null;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      fetchReservations(),
      fetchBols(),
      fetchServices(),
      fetchPacking(),
      fetchBuildUp(),
      fetchCargoTypes()
    ]);
  };

  const fetchReservations = async () => {
    try {
      const res = await api.get('/reservations');
      setReservations(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching reservations:', err);
    }
  };

  const deleteReservation = async (reservationId) => {
    if (!window.confirm('Are you sure you want to delete this reservation?')) return;
    try {
      await api.del(`/reservations/${reservationId}`);
      setReservations((prev) => prev.filter((r) => r.id !== reservationId));
      if (selectedReservation?.id === reservationId) {
        setSelectedReservation(null);
      }
    } catch (err) {
      console.error('Error deleting reservation:', err);
      alert('Error deleting reservation');
    }
  };

  const deleteBol = async (bolId) => {
    if (!window.confirm('Are you sure you want to delete this AWB?')) return;
    try {
      await api.del(`/bols/${bolId}`);
      setBols((prev) => prev.filter((b) => b.id !== bolId));
      if (editingBol?.id === bolId) {
        cancelEdit();
      }
    } catch (err) {
      console.error('Error deleting AWB:', err);
      alert('Error deleting AWB');
    }
  };

  // دالة لمعالجة Cargo Type إذا كان مخزناً كـ JSON
  const formatCargoType = (val) => {
    if (!val) return "";
    if (val.startsWith("[")) { try { return JSON.parse(val)[0]; } catch(e) { return val; } }
    return val;
  };

  const fetchBols = async () => {
    try {
      const res = await api.get('/bols');
      setBols(
        Array.isArray(res)
          ? res.map((bol) => ({ ...bol, fees: parseJsonValue(bol.fees) }))
          : []
      );
    } catch (err) {
      console.error('Error fetching AWBs:', err);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get('/services');
      if (Array.isArray(res)) {
        const mapped = res.map(s => {
          const flattenedPrices = {};
          if (s.prices) {
            Object.keys(s.prices).forEach(key => {
              flattenedPrices[key.toLowerCase()] = s.prices[key];
            });
          }
          return { ...s, ...flattenedPrices };
        });
        setServicesList(mapped);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const fetchPacking = async () => {
    try {
      const res = await api.get('/packing');
      setPackingItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching packing:', err);
    }
  };

  const fetchBuildUp = async () => {
    try {
      const res = await api.get('/build-up');
      setBuildUpItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching build-up:', err);
    }
  };

  const fetchCargoTypes = async () => {
    try {
      const res = await api.get('/cargo-types');
      setCargoTypes(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error fetching cargo types:', err);
    }
  };

  const selectReservation = (reservation) => {
    // FIX 6: Guard against data not loaded yet
    if (!servicesList.length && !packingItems.length && !buildUpItems.length) {
      console.warn('Data not fully loaded yet');
    }

    setEditingBol(null);
    setIsDirectIssuance(false);
    setSelectedReservation(reservation);

    const cargoTypesArr = Array.isArray(reservation.cargo_type)
      ? reservation.cargo_type
      : (typeof reservation.cargo_type === 'string'
        ? JSON.parse(reservation.cargo_type || '[]')
        : []);

    const servicesArr = Array.isArray(reservation.service_id)
      ? reservation.service_id.map(id => servicesList.find(s => s.id == id)).filter(Boolean)
      : [];

    const packingArr = Array.isArray(reservation.packing_item_id)
      ? reservation.packing_item_id.map(id => packingItems.find(p => p.id == id)).filter(Boolean)
      : [];

    const buildUpArr = Array.isArray(reservation.build_up_item_id)
      ? reservation.build_up_item_id.map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
      : [];

    const formatDate = (isoDate) => {
      if (!isoDate) return '';
      return isoDate.split('T')[0];
    };

    const initialForm = {
      ...emptyForm, // Start with empty form to clear previous data
      bolReference: reservation.bol_reference || '',
      bolNature: reservation.bol_nature || '23',
      notifyName: '',
      notifyAddress: '',
      customerName: reservation.customer_name || '',
      numberOfPackages: reservation.number_of_packages || '',
      packageTypeCode: reservation.package_type_code || 'PK',
      grossMass: reservation.gross_mass || '',
      shippingMarks: reservation.shipping_marks || 'A',
      goodsDescription: reservation.goods_description || '',
      cargoTypes: parseIdArray(reservation.cargo_types), // Use parseIdArray for safety
      services: parseIdArray(reservation.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean),
      packingItems: parseIdArray(reservation.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean),
      buildUpItems: parseIdArray(reservation.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean),
      // Default cargoType/serviceType for display if needed, but actual selections are in arrays
      cargoType: parseIdArray(reservation.cargo_types)[0] || '',
      serviceType: parseIdArray(reservation.service_ids)[0] ? servicesList.find(s => s.id == parseIdArray(reservation.service_ids)[0])?.type || '' : '',
      serviceId: parseIdArray(reservation.service_ids)[0] || null,
      bolTypeCode: reservation.bol_type_code || (reservation.homes?.length > 0 ? '741' : '740'),

      storageStartDate: formatDate(reservation.storage_start_date),
      storageEndDate: formatDate(reservation.storage_end_date),
      homes: [],
      day: false

    };
    setForm(initialForm);
  };
  

  const editBOL = (bol) => {
    setSelectedReservation(null);
    setEditingBol(bol);
    
    const serviceIds = parseIdArray(bol.service_ids);
    const packingIds = parseIdArray(bol.packing_item_ids);
    const buildUpIds = parseIdArray(bol.build_up_item_ids);
    const cargoTypesArr = parseIdArray(bol.cargo_types);
    // ✅ Ensure storage dates are correctly formatted for date inputs
    const formatDate = (isoDate) => {
      if (!isoDate) return '';
      if (typeof isoDate === 'string' && isoDate.includes('T')) {
        return isoDate.split('T')[0];
      }
      return isoDate;
    };

    const formattedStorageStartDate = formatDate(bol.storage_start_date);
    const formattedStorageEndDate = formatDate(bol.storage_end_date);

    const servicesArr = serviceIds.length > 0
      ? serviceIds.map(id => servicesList.find(s => s.id == id)).filter(Boolean)
      : (bol.service_id ? [servicesList.find(s => s.id == bol.service_id)].filter(Boolean) : []);

    const packingArr = packingIds.length > 0
      ? packingIds.map(id => packingItems.find(p => p.id == id)).filter(Boolean)
      : (bol.packing_item_id ? [packingItems.find(p => p.id == bol.packing_item_id)].filter(Boolean) : []);

    const buildUpArr = buildUpIds.length > 0
      ? buildUpIds.map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
      : (bol.build_up_item_id ? [buildUpItems.find(b => b.id == bol.build_up_item_id)].filter(Boolean) : []);


    setForm({
      ...emptyForm,
      bolReference: bol.bol_reference || '',
      bolNature: bol.bol_nature || '',
      bolTypeCode: bol.bol_type_code || '',
      customerName: bol.customer_name || bol.customer_name || '', // Use customer_name from BOL
      notifyName: bol.notify_name || '',
      notifyAddress: bol.notify_address || '',
      numberOfPackages: bol.number_of_packages || '',
      packageTypeCode: bol.package_type_code || '',
      grossMass: bol.gross_mass || '',
      shippingMarks: bol.shipping_marks || '',
      goodsDescription: bol.goods_description || '',
      cargoTypes: cargoTypesArr,
      services: servicesArr.map(s => ({ ...s, showPacking: false })), // Ensure showPacking is false initially
      packingItems: packingArr,
      buildUpItems: buildUpArr,
      cargoType: cargoTypesArr[0] || '', // Use first cargo type for display if needed
      serviceType: bol.service_type || (servicesArr[0]?.type || ''),
      serviceId: bol.service_id || (servicesArr[0]?.id || null),
      storageStartDate: formattedStorageStartDate,
      storageEndDate: formattedStorageEndDate,
      day: bol.day === 1 || bol.day === true,
      homes: Array.isArray(bol.homes) ? bol.homes.map(h => {
        const hServiceIds = parseIdArray(h.service_ids);
        const hPackingIds = parseIdArray(h.packing_item_ids);
        const hBuildUpIds = parseIdArray(h.build_up_item_ids);

        return {
          ...h,
          tempId: h.id,
          bolReference: h.bol_reference,
          grossMass: h.gross_mass,
          numberOfPackages: h.number_of_packages,
          cargoType: h.cargo_type,
          goodsDescription: h.goods_description,
          services: hServiceIds.length > 0
            ? hServiceIds.map(id => servicesList.find(s => s.id == id)).filter(Boolean)
            : (h.service_id ? [servicesList.find(s => s.id == h.service_id)].filter(Boolean) : []),
          packingItems: hPackingIds.length > 0 // ✅ Fix: Use hPackingIds
            ? hPackingIds.map(id => packingItems.find(p => p.id == id)).filter(Boolean)
            : (h.packing_item_id ? [packingItems.find(p => p.id == h.packing_item_id)].filter(Boolean) : []),
          buildUpItems: hBuildUpIds.length > 0
            ? hBuildUpIds.map(id => buildUpItems.find(b => b.id == id)).filter(Boolean) // Fix: Use hBuildUpIds
            : (h.build_up_item_id ? [buildUpItems.find(b => b.id == h.build_up_item_id)].filter(Boolean) : [])
        };
      }) : (bol.homes || []) // Ensure homes array is always present
    });
  };

  const startDirectIssuance = () => {
    setEditingBol(null);
    setSelectedReservation(null);
    setIsDirectIssuance(true);
    setForm(emptyForm);
  };

  const cancelEdit = () => {
    setEditingBol(null);
    setSelectedReservation(null);
    setIsDirectIssuance(false);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const returnForReview = async (bolId) => {
    if (!window.confirm('Are you sure you want to return this AWB for review? It will be sent back to the Operator Application screen.')) return;
    try {
      await api.put(`/bols/${bolId}`, { status: 'operator_review' });
      await fetchBols();
      if (editingBol?.id === bolId) {
        cancelEdit();
      }
      alert('AWB returned for review successfully ✅');
    } catch (err) {
      console.error('Error returning AWB for review:', err);
      alert('Error returning AWB for review');
    }
  };

  // أتمتة تجميع الأوزان والطرود من الهاوس إلى الماستر
  useEffect(() => {
    if (form.homes.length > 0) {
      const totalPkgs = form.homes.reduce((sum, h) => sum + (Number(h.numberOfPackages) || 0), 0); // Fix: Use Number(h.numberOfPackages)
      const totalWeight = form.homes.reduce((sum, h) => sum + (Number(h.grossMass) || 0), 0);

      if (totalPkgs.toString() !== form.numberOfPackages || totalWeight.toString() !== form.grossMass) {
        setForm(prev => ({
          ...prev,
          numberOfPackages: totalPkgs > 0 ? totalPkgs.toString() : prev.numberOfPackages,
          grossMass: totalWeight > 0 ? totalWeight.toString() : prev.grossMass
        }));
      }
    }
    // If no homes, ensure master packages/weight are not stuck from previous homes
    else if (form.homes.length === 0 && (form.numberOfPackages !== '' || form.grossMass !== '')) {
      setForm(prev => ({ ...prev, numberOfPackages: '', grossMass: '' }));
    }
  }, [form.homes]);

  const selectedService = servicesList.find((s) => s.id == form.serviceId)
    || findServiceByTypeAndWeight(form.serviceType || form.cargoType, Number(form.grossMass) || 0);

  const uniqueServiceTypes = [...new Set(servicesList.map(s => s.type).filter(Boolean))];

  const handleAutoServiceSelect = (type) => {
    const weight = Number(form.grossMass) || 0;
    if (weight <= 0) {
      alert('Please enter Gross Mass (Weight) first to determine the correct classification.');
      return;
    }
    const matched = findServiceByTypeAndWeight(type, weight);
    if (matched) {
      if (!form.services.some(s => s.type === matched.type)) {
        setForm(prev => ({ ...prev, services: [...prev.services, { ...matched, showPacking: false, selectedPackingId: null }] }));
      } else {
        alert('This service type is already selected.');
      }
    }
  };

  // دالة الإضافة التلقائية للخدمات داخل بوليصة هاوس محددة
  const handleHouseAutoServiceSelect = (houseIdx, type) => {
    const house = form.homes[houseIdx];
    const weight = Number(house.grossMass) || 0;
    if (weight <= 0) return alert('Please enter Weight for this House AWB first.');

    const matched = findServiceByTypeAndWeight(type, weight);
    if (matched) {
      const newHomes = [...form.homes];
      if (!newHomes[houseIdx].services.some(s => s.type === matched.type)) {
        newHomes[houseIdx].services = [...newHomes[houseIdx].services, { ...matched, showPacking: false, selectedPackingId: null }];
        setForm(prev => ({ ...prev, homes: newHomes }));
      }
    }
  };

  useEffect(() => {
    const quantity = Number(form.numberOfPackages) || 0;
    const weight = Number(form.grossMass) || 0;

    // دالة داخلية لحساب رسوم بوليصة واحدة
    const calculateSingleBolFees = (fWeight, fQty, fCargoType, fServices, fPacking, fBuildUp, fAwbCount) => {
      let sFee = 0, pFee = 0, bFee = 0;
      const priceKey = normalizeType(fCargoType || 'gcr');
      
      fServices.forEach(s => {
        const rate = (typeof s[priceKey] === 'string' && s[priceKey].toLowerCase() === 'free') ? 0 : Number(s[priceKey] || 0);
        if (s.rate_type === 'per_kg') sFee += rate * fWeight;
        else if (s.rate_type === 'per_ton') sFee += rate * (fWeight / 1000);
        else if (s.rate_type === 'per_awb') sFee += rate * fAwbCount;
        else sFee += rate;
      });

      fPacking.forEach(p => pFee += (Number(p.price) || 0) * (Number(fQty) || 1));
      fBuildUp.forEach(b => bFee += Number(b.price) || 0);

      return { service: sFee, packing: pFee, buildUp: bFee, total: sFee + pFee + bFee };
    };

    // 1. حساب رسوم الماستر
    // إذا كان هنالك بوالص هاوس، نستخدم عددهم كمضاعف، وإلا نستخدم 1 (للبوالص المباشرة)
    const masterAwbMultiplier = form.homes?.length > 0 ? form.homes.length : 1;
    const masterFees = calculateSingleBolFees(weight, quantity, form.cargoType, form.services, form.packingItems, form.buildUpItems, masterAwbMultiplier);

    // 2. حساب رسوم كل هاوس وتحديث المجهر (form.homes) بالرسوم المحسوبة لكل واحدة
    let totalHouseFees = { service: 0, packing: 0, buildUp: 0, total: 0 };
    const updatedHomes = form.homes.map(h => {
      // كل بوليصة هاوس تُحسب كبوليصة واحدة (Multiplier = 1)
      const fees = calculateSingleBolFees(Number(h.grossMass), Number(h.numberOfPackages), h.cargoType, h.services, h.packingItems, h.buildUpItems, 1);
      totalHouseFees.service += fees.service;
      totalHouseFees.packing += fees.packing;
      totalHouseFees.buildUp += fees.buildUp;
      totalHouseFees.total += fees.total;
      return { ...h, fees }; // تخزين الرسوم داخل جسم الهاوس للعرض
    });

    // منع التحديث اللانهائي: قارن فقط القيم الجوهرية للرسوم
    const feesChanged = updatedHomes.some((h, idx) => h.fees.total !== form.homes[idx]?.fees?.total);
    if (feesChanged) {
      setForm(prev => ({ ...prev, homes: updatedHomes }));
    }

    setCalculatedFees({
      service: masterFees.service + totalHouseFees.service,
      packing: masterFees.packing + totalHouseFees.packing,
      buildUp: masterFees.buildUp + totalHouseFees.buildUp,
      total: masterFees.total + totalHouseFees.total
    });
  }, [
    form.services,
    form.packingItems,
    form.buildUpItems,
    form.serviceType,
    form.cargoType,
    form.grossMass,
    form.numberOfPackages,
    form.homes,
    servicesList,
    packingItems,
    buildUpItems
  ]);

  useEffect(() => {
    if (!form.serviceType) return;
    const weight = Number(form.grossMass) || 0;
    const matched = findServiceByTypeAndWeight(form.serviceType, weight);
    if (matched && matched.id != form.serviceId) {
      setForm((prev) => ({ ...prev, serviceId: matched.id }));
    }
  }, [form.serviceType, form.grossMass, servicesList]);

  useEffect(() => {
    if (form.serviceId && !form.serviceType) {
      const service = servicesList.find((s) => s.id == form.serviceId);
      if (service) {
        setForm((prev) => ({ ...prev, serviceType: service.type }));
      }
    }
  }, [form.serviceId, form.serviceType, servicesList]);

  const saveBOL = async () => {
    let reservationId = editingBol?.reservation_id || selectedReservation?.id;
    const isDirect = isDirectIssuance && !reservationId;

    if (!form.bolReference) return alert('Please enter AWB Reference');

    if (isDirect) {
      if (!form.customerName) return alert('Please enter Customer Name');
      if (!form.cargoTypes || form.cargoTypes.length === 0) return alert('Please select at least one Cargo Type');
      if (!form.services || form.services.length === 0) return alert('Please select at least one Service');
      if (!form.numberOfPackages || !form.grossMass) return alert('Please fill in actual packages and weight');
    }

    try {
      if (isDirect) {
        const resPayload = {
          customerName: form.customerName,
          cargoTypes: form.cargoTypes,
          services: form.services.map(s => s.id),
          packingItems: form.packingItems.map(p => p.id),
          buildUpItems: form.buildUpItems.map(b => b.id),
          weight: form.grossMass,
          packages: form.numberOfPackages,
          storage_start_date: form.storageStartDate,
          storage_end_date: form.storageEndDate,
          notes: `Direct Issuance for AWB: ${form.bolReference}`
        };
        const createdRes = await api.post('/reservations', resPayload);
        reservationId = createdRes.id;
      }

      const derivedServiceId = form.services.length > 0
        ? form.services[0].id
        : (form.serviceId || findServiceByTypeAndWeight(form.serviceType, Number(form.grossMass))?.id || null);

      const payload = {
        reservation_id: reservationId,
        bol_reference: form.bolReference,
        status: 'services_assigned', // ✅ التأكد من إرسال الحالة للسيرفر
        bol_nature: form.bolNature,
        bol_type_code: form.bolTypeCode,
        number_of_packages: form.numberOfPackages,
        package_type_code: form.packageTypeCode,
        gross_mass: form.grossMass,
        shipping_marks: form.shippingMarks,
        goods_description: form.goodsDescription,
        cargo_types: form.cargoTypes, // Use directly from form
        service_ids: form.services.map(s => s.id), // Use directly from form
        packing_item_ids: form.packingItems.map(p => p.id), // Use directly from form
        build_up_item_ids: form.buildUpItems.map(b => b.id), // Use directly from form
        homes: form.homes,
        service_id: derivedServiceId,
        packing_item_id: form.packingItems.length > 0 ? form.packingItems[0].id : null,
        build_up_item_id: form.buildUpItems.length > 0 ? form.buildUpItems[0].id : null,
        storage_start_date: form.storageStartDate,
        storage_end_date: form.storageEndDate,
        day: form.day,
        fees: calculatedFees
      };

      const enhanced = {
        ...payload,
        fees: calculatedFees,
        status: 'services_assigned', // ✅ تحديث الحالة في الواجهة المحلية فوراً
        service_type: form.serviceType || (form.services.length > 0 ? form.services[0].type : ''),
        service_name: selectedService?.name || (form.serviceType || ''),
        service_classification: selectedService?.classification || '',
        cargo_types: form.cargoTypes || [],
        services: form.services || [],
        packingItems: form.packingItems || [],
        buildUpItems: form.buildUpItems || []
      };

      let saved;
      if (editingBol) {
        await api.put(`/bols/${editingBol.id}`, payload);
        saved = { ...editingBol, ...enhanced };
        setBols((prev) => prev.map((item) => item.id === editingBol.id ? saved : item));
        alert('BOL updated successfully');
      } else {
        const created = await api.post('/bols', payload);
        saved = { ...created, ...enhanced };
        setBols([...bols, saved]);
        setReservations((prev) => prev.filter((r) => r.id !== selectedReservation?.id));
        alert('BOL issued successfully');
      }

      setEditingBol(null);
      setSelectedReservation(null);
      setIsDirectIssuance(false);
      setForm(emptyForm);

    } catch (err) {
      console.error('Error saving BOL:', err);
      alert('Error issuing BOL');
    }
  };

  const printReceipt = async (bol) => {
    try {
      // إذا كان ماستر، نقوم بتحديث حالة الدفع له ولجميع الهاوسات التابعة له
      const updated = await api.post(`/bols/${bol.id}/pay`); 
      
      // نقوم بتجهيز بيانات مجمعة للطباعة
      const printData = {
        ...updated,
        fees: parseJsonValue(updated.fees),
        homes: (bol.homes || []).map(h => ({
          ...h,
          fees: parseJsonValue(h.fees)
        }))
      };

      setBols(prev => prev.map(b => b.id === bol.id ? { ...b, paid: 1 } : b));
      generateReceiptPdf({ bol: printData });
    } catch (err) {
      console.error('Error processing payment:', err);
      alert('Error processing payment or extracting receipt');
    }
  };

  const printInvoice = (bol) => {
    const printData = {
      ...bol,
      homes: (bol.homes || []).map(h => ({
        ...h,
        fees: parseJsonValue(h.fees)
      }))
    };
    generateBolInvoicePdf({ bol: printData });
  };

  return (
    <div className="page">
      <h2>AWB Issuance</h2>

      {/* <div style={{ marginBottom: '20px' }}>
        <button className="btn btn-primary" onClick={startDirectIssuance}>
          + New Direct AWB (Acceptance &amp; Issuance)
        </button>
      </div> */}

      {(selectedReservation || editingBol || isDirectIssuance) && (
        <div className="card">
          <h3>
            {editingBol
              ? `Edit AWB ${editingBol.bol_reference}`
              : selectedReservation
                ? `Issue AWB for Reservation #${selectedReservation.id}`
                : 'New AWB Issuance'}
          </h3>

          <div className="grid grid-auto">

            {isDirectIssuance && !editingBol && (
              <div className="field">
                <label>Customer Name (Acceptance) *</label>
                <input
                  type="text"
                  value={form.customerName}
                  onChange={(e) => handleFormChange('customerName', e.target.value)}
                  placeholder="Who brought the goods?"
                />
              </div>
            )}

            <div className="field">
              <label>AWB Reference *</label>
              <input
                type="text"
                value={form.bolReference}
                readOnly
                style={{ background: '#f5f5f5' }}
              />
            </div>

            {/* FIX 1 & 3: BOL Type Code field properly closed */}
            <div className="field">
              <label>AWB Type Code</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={form.homes.length > 0 ? '741' : '740'}
                  readOnly
                  style={{ background: '#f8fafc', fontWeight: 'bold', color: '#2e7d32', width: '100%' }}
                />
                <small style={{ fontSize: '10px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                  {form.homes.length > 0 ? 'Master (with Houses)' : 'Direct AWB'}
                </small>
              </div>
            </div>





            <div className="field">
              <label>Number of Packages</label>
              <input type="number" readOnly value={form.numberOfPackages} onChange={(e) => handleFormChange('numberOfPackages', e.target.value)} />
            </div>

            <div className="field">
              <label>Package Type Code</label>
              <input type="text" readOnly value={form.packageTypeCode} onChange={(e) => handleFormChange('packageTypeCode', e.target.value)} />
            </div>

            <div className="field">
              <label>Gross Mass</label>
              <input type="number" readOnly value={form.grossMass} onChange={(e) => handleFormChange('grossMass', e.target.value)} />
            </div>

            {/* <div className="field">
              <label>Shipping Marks</label>
              <input type="text" value={form.shippingMarks} onChange={(e) => handleFormChange('shippingMarks', e.target.value)} />
            </div> */}

            <div className="field">
              <label>Goods Description</label>
              <input type="text" readOnly value={form.goodsDescription} onChange={(e) => handleFormChange('goodsDescription', e.target.value)} />
            </div>

            {/* Cargo Types */}
            <div className="field">
              <label>Cargo Types</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {form.cargoTypes.map((type, idx) => (
                  <span key={idx} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                    {type}
                    <button
                      onClick={() => setForm(f => ({ ...f, cargoTypes: f.cargoTypes.filter(t => t !== type) }))}
                      style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                    >×</button>
                  </span>
                ))}
              </div>
              <div className="select-field">
              <select value="" onChange={e => {
                const val = e.target.value;
                if (val && !form.cargoTypes.includes(val)) {
                  setForm(f => ({ ...f, cargoTypes: [...f.cargoTypes, val] }));
                }
              }}>
                <option value="">Select Cargo Type</option>
                {cargoTypes.filter(ct => !form.cargoTypes.includes(ct.name)).map(ct => (
                  <option key={ct.id} value={ct.name}>{ct.name}</option>
                ))}
              </select>
              </div>
            </div>

            {/* Services */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Services Selection (Auto-identifies classification by weight)</label>
              <div className="selected-items-container" style={{ flexDirection: 'column', gap: '10px', background: '#fff', padding: '10px' }}>
                {form.services.length === 0 && (
                  <span className="hint-text" style={{ padding: '5px' }}>No services selected yet...</span>
                )}
                {form.services.map(srvc => (
                  <div key={srvc.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><strong>{srvc.type}:</strong> {srvc.classification}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#2e7d32', fontWeight: 'bold' }}>
                          <input
                            type="checkbox"
                            checked={srvc.showPacking || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const newServices = form.services.map(s => s.id === srvc.id ? { ...s, showPacking: checked } : s);
                              setForm(f => {
                                const updatedPacking = (!checked && srvc.selectedPackingId) 
                                  ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId) 
                                  : f.packingItems;
                                return { ...f, services: newServices, packingItems: updatedPacking };
                              });
                            }}
                          /> Add Packing
                        </label>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => {
                            setForm(f => {
                              const updatedServices = f.services.filter(s => s.id !== srvc.id);
                              const updatedPacking = srvc.selectedPackingId
                                ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                                : f.packingItems;
                              return { ...f, services: updatedServices, packingItems: updatedPacking };
                            });
                          }}
                        >✕</button>
                      </div>
                    </div>
                    {srvc.showPacking && (
                      <div className="select-field" style={{ marginTop: '5px' }}>
                        {srvc.selectedPackingId ? (
                          <div className="item-chip" style={{ background: '#e2e8f0', justifyContent: 'space-between', display: 'flex', padding: '5px 10px' }}>
                            <span><strong>Packing:</strong> {(() => {
                              const p = packingItems.find(item => item.id == srvc.selectedPackingId);
                              return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                            })()}</span>
                            <button type="button" className="remove-btn" onClick={() => {
                              setForm(f => ({
                                ...f,
                                services: f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: null } : s),
                                packingItems: f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                              }));
                            }}>✕</button>
                          </div>
                        ) : (
                          <select value="" onChange={e => {
                            const item = packingItems.find(p => p.id == e.target.value);
                            if (item) {
                              setForm(f => {
                                const updatedServices = f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: item.id } : s);
                                const updatedPacking = !f.packingItems.some(p => p.id == item.id) ? [...f.packingItems, item] : f.packingItems;
                                return { ...f, services: updatedServices, packingItems: updatedPacking };
                              });
                            }
                          }}>
                            <option value="">Select Master Packing Type...</option>
                            {packingItems.filter(p => !form.packingItems.some(selected => selected.id === p.id)).map(p => (
                              <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="select-field">
                <select value="" onChange={e => handleAutoServiceSelect(e.target.value)}>
                  <option value="">+ Add Service Type...</option>
                  {uniqueServiceTypes
                    .filter(t => !form.services.some(s => s.type === t))
                    .map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                </select>
              </div>
            </div>

            {/* Master Packing Display & Build-up (Moved up here) */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Master Packing Summary</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, minHeight: '35px', padding: '5px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                {form.packingItems.length === 0 && <span className="hint-text">No packing selected</span>}
                {form.packingItems.map(item => (
                  <span
                    key={item.id}
                    style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', fontSize: '11px' }}
                  >
                    {item.type}{item.classification ? ` - ${item.classification}` : ''}
                    <button
                      onClick={() => setForm(f => ({ ...f, packingItems: f.packingItems.filter(p => p.id !== item.id) }))}
                      style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                    >×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Build-up */}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Build-up Selection</label>
              <div className="selected-items-container">
                {form.buildUpItems.map(item => (
                  <div key={item.id} className="item-chip">
                    {item.name}
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => setForm(f => ({ ...f, buildUpItems: f.buildUpItems.filter(b => b.id !== item.id) }))}
                    >✕</button>
                  </div>
                ))}
              </div>
              <div className="select-field">
              <select value="" onChange={e => {
                const selectedId = e.target.value;
                const fullObject = buildUpItems.find(b => b.id == selectedId);
                if (fullObject && !form.buildUpItems.some(b => b.id == selectedId)) {
                  setForm(f => ({ ...f, buildUpItems: [...f.buildUpItems, fullObject] }));
                }
              }}>
                <option value="">None</option>
                {buildUpItems
                  .filter(b => !form.buildUpItems.some(selected => selected.id === b.id))
                  .map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
              </select>
              </div>
            </div>

            {/* House AWBs — FIX 1: removed orphaned JSX comment block */}
            <div className="field" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
              <div className="card-header-row">
                <label style={{ fontSize: '1.2rem', color: '#1b5e20' }}>House AWBs (Consolidation Details)</label>
              </div>
              <div className="selected-items-container" style={{ flexDirection: 'column', background: '#fff' }}>
                {form.homes.length === 0 && (
                  <span className="hint-text">No houses attached to this master.</span>
                )}
                {form.homes.map((house, idx) => (
                  <div key={house.tempId} className="card" style={{ marginBottom: '15px', border: '1px solid #0288d1', borderLeft: '6px solid #0288d1', padding: '20px', background: '#f0f9ff' }}>
                    {/* <div className="card-header-row">
                       <strong style={{ color: '#0288d1' }}><span className="badge" style={{background: '#0288d1', color: 'white'}}>HOUSE</span> BOL #{idx + 1}</strong>
                       <button type="button" className="btn btn-danger" onClick={() => removeHouseBol(house.tempId)}>Remove House</button>
                    </div> */}
                    
                    <div className="grid grid-auto small-gap">
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Cargo Type</label>
                        <div className="select-field">
                        <select value={house.cargoType || ""} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].cargoType = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }}>
                          <option value="">-- Select --</option>
                          {cargoTypes.map(ct => <option key={ct.id} value={ct.name}>{ct.name}</option>)}
                        </select>
                        </div>
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Ref *</label>
                        <input type="text" readOnly value={house.bolReference} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].bolReference = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Weight (kg) *</label>
                        <input type="number" readOnly value={house.grossMass} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].grossMass = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>House Packages *</label>
                        <input type="number" readOnly value={house.numberOfPackages} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].numberOfPackages = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>Goods Description</label>
                        <input type="text" readOnly value={house.goodsDescription} onChange={(e) => {
                          const newHomes = [...form.homes];
                          newHomes[idx].goodsDescription = e.target.value;
                          setForm(f => ({ ...f, homes: newHomes }));
                        }} />
                      </div>
                    </div>

                    <div className="grid grid-auto small-gap" style={{ gridColumn: '1 / -1' }}>
                      <div className="field">
                        <label>House Build-up</label>
                        <div className="selected-items-container">
                          {house.buildUpItems?.map(b => (
                            <div key={b.id} className="item-chip" style={{ background: '#fff' }}>
                              <small>{b.name}</small>
                              <button type="button" className="remove-btn" onClick={() => {
                                const newHomes = [...form.homes];
                                newHomes[idx].buildUpItems = house.buildUpItems.filter(item => item.id !== b.id);
                                setForm(f => ({ ...f, homes: newHomes }));
                              }}>✕</button>
                            </div>
                          ))}
                        </div>
                        <div className="select-field">
                        <select value="" onChange={e => {
                          const item = buildUpItems.find(b => b.id == e.target.value);
                          if (item) {
                            const newHomes = [...form.homes];
                            newHomes[idx].buildUpItems = [...(house.buildUpItems || []), item];
                            setForm(f => ({ ...f, homes: newHomes }));
                          }
                        }}>
                          <option value="">+ Add Build-up...</option>
                          {buildUpItems.filter(b => !house.buildUpItems?.some(hi => hi.id === b.id)).map(b => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                        </div>
                      </div>
                    </div>

                    {/* الخدمات الخاصة بالهاوس */}
                    <div className="grid grid-auto small-gap" style={{ marginTop: '10px', gridColumn: '1 / -1' }}>
                      <div className="field">
                        <label>House Services & Packing</label>
                        <div className="selected-items-container" style={{ flexDirection: 'column', gap: '8px' }}>
                          {house.services.map(srv => (
                            <div key={srv.id} style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd', width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <small><strong>{srv.type}:</strong> {srv.classification}</small>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: '#0288d1', cursor: 'pointer' }}>
                                    <input 
                                      type="checkbox" 
                                      checked={srv.showPacking || false} 
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setForm(f => {
                                          const newHomes = [...f.homes];
                                          newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, showPacking: checked } : s);
                                          if (!checked && srv.selectedPackingId) {
                                            newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                          }
                                          return { ...f, homes: newHomes };
                                        });
                                      }} 
                                    /> Add Packing
                                  </label>
                                  <button type="button" className="remove-btn" onClick={() => {
                                    setForm(f => {
                                      const newHomes = [...f.homes];
                                      newHomes[idx].services = house.services.filter(s => s.id !== srv.id);
                                      if (srv.selectedPackingId) {
                                        newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                      }
                                      return { ...f, homes: newHomes };
                                    });
                                  }}>✕</button>
                                </div>
                              </div>
                              {srv.showPacking && (
                                <div className="select-field" style={{ marginTop: '5px' }}>
                                  {srv.selectedPackingId ? (
                                    <div className="item-chip" style={{ background: '#f1f5f9', justifyContent: 'space-between', display: 'flex', padding: '4px 8px' }}>
                                      <small><strong>Packing:</strong> {(() => {
                                        const p = packingItems.find(item => item.id == srv.selectedPackingId);
                                        return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                                      })()}</small>
                                      <button type="button" className="remove-btn" onClick={() => {
                                        const newHomes = [...form.homes];
                                        newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: null } : s);
                                        newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                        setForm(f => ({ ...f, homes: newHomes }));
                                      }}>✕</button>
                                    </div>
                                  ) : (
                                    <select value="" onChange={e => {
                                      const item = packingItems.find(p => p.id == e.target.value);
                                      if (item) {
                                        const newHomes = [...form.homes];
                                        newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: item.id } : s);
                                        if (!house.packingItems.some(p => p.id == item.id)) {
                                          newHomes[idx].packingItems = [...house.packingItems, item];
                                        }
                                        setForm(f => ({ ...f, homes: newHomes }));
                                      }
                                    }}>
                                      <option value="">Select House Packing...</option>
                                      {packingItems.filter(p => !house.packingItems.some(hi => hi.id === p.id)).map(p => (
                                        <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="select-field" style={{ marginTop: '8px' }}>
                        <select value="" onChange={e => handleHouseAutoServiceSelect(idx, e.target.value)}>
                          <option value="">+ Add Service to House...</option>
                          {uniqueServiceTypes.filter(t => !house.services.some(s => s.type === t)).map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        </div>
                      </div>

                      <div className="field">
                        <label>House Packing Summary</label>
                        <div className="selected-items-container">
                          {house.packingItems.map(p => (
                            <div key={p.id} className="item-chip" style={{ background: '#fff' }}>
                              <small>{p.type}{p.classification ? ` - ${p.classification}` : ''}</small>
                              <button type="button" className="remove-btn" onClick={() => {
                                const newHomes = [...form.homes];
                                newHomes[idx].packingItems = house.packingItems.filter(item => item.id !== p.id);
                                setForm(f => ({ ...f, homes: newHomes }));
                              }}>✕</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Storage Dates — FIX 2: both fields wrapped in their own <div className="field"> */}
            <div className="field">
              <label>Storage Start Date</label>
              <input
                type="date"
                value={form.storageStartDate}
                onChange={(e) => handleFormChange('storageStartDate', e.target.value)}
              />
            </div>

            <div className="field">
              <label>Storage End Date</label>
              <input
                type="date"
                value={form.storageEndDate}
                onChange={(e) => handleFormChange('storageEndDate', e.target.value)}
              />
            </div>

            {/* <div className="field">
              <label>
                <input
                  type="checkbox"
                  checked={form.day}
                  onChange={(e) => handleFormChange('day', e.target.checked)}
                />
                {' '}Subtract Day
              </label>
            </div> */}

{/* <div className="field" style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
  <button
    type="button"
    className="btn btn-secondary"
    onClick={addHouseBol}
    style={{
      width: '100%',
      padding: '10px',
      fontWeight: 'bold',
      backgroundColor: '#2e7d32',
      color: '#fff',
      border: 'none'
    }}
  >
    + Add House AWB
  </button>
</div> */}

          </div>{/* END grid grid-auto — FIX 3: properly closed */}

          <div className="calc-result">
            <div>Service Fee: ${calculatedFees.service.toFixed(2)}</div>
            <div>Packing Fee: ${calculatedFees.packing.toFixed(2)}</div>
            <div>Build-up Fee: ${calculatedFees.buildUp.toFixed(2)}</div>
            <div>Total: ${calculatedFees.total.toFixed(2)}</div>
          </div>

          <div className="actions-row">
            <button className="btn btn-primary" onClick={saveBOL}>
              {editingBol ? 'Update AWB' : 'Issue AWB'}
            </button>
            {editingBol && (
              <button className="btn btn-secondary" style={{ marginLeft: '10px' }} onClick={cancelEdit}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <h3>AWBs Awaiting Services</h3>
        <table className="grid-table" style={{ marginBottom: '30px' }}>
          <thead>
            <tr>
              <th>AWB Reference</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bols.filter(b => !b.paid && b.status !== 'services_assigned' && (b.returned_for_review == null || b.returned_for_review == 0)).map(b => (
              <tr key={b.id}>
                <td>{b.bol_reference}</td>
                <td><span className="badge badge-dg">{b.status === 'operator_review' ? 'Returned for Review' : 'Pending Services'}</span></td>
                <td>
                  <button className="btn btn-warning" onClick={() => editBOL(b)}>Select Services</button>
                  <button className="btn btn-danger" style={{ marginLeft: '8px' }} onClick={() => returnForReview(b.id)}>Return to Operator</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={{ color: '#2e7d32', borderTop: '2px solid #eee', paddingTop: '20px' }}>Issued & Ready for Printing</h3>
        <table className="grid-table">
          <thead>
            <tr>
              <th>AWB Reference</th>
              <th>Packages</th>
              <th>Weight</th>
              <th>Total Fees</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bols.filter(b => !b.paid && b.status === 'services_assigned').map(b => (
              <tr key={b.id}>
                <td>{b.bol_reference}</td>
                <td>{b.number_of_packages}</td>
                <td>{b.gross_mass}</td>
                <td>${(b.fees?.total || 0).toFixed(2)}</td>
                <td>
                  <>
                    <button className="btn" onClick={() => printReceipt(b)}>Print Receipt</button>
                    <button className="btn btn-secondary" style={{ marginLeft: '8px' }} onClick={() => printInvoice(b)}>Print Invoice</button>
                  </>
                  <button className="btn btn-warning" style={{ marginLeft: '8px' }} onClick={() => editBOL(b)}>Select Services</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BOLIssuanceScreen;